'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { ConfigProvider } from 'antd'
import { PageProvider } from '@/shared/providers/context/PageContext'
import { LoadingProvider } from '@/shared/providers/context/LoadingContext'
import { ClientDeskSessionProvider } from '@/shared/providers/context/ClientDeskSessionContext'
import { useTheme } from '@/shared/theme'
import { ApiProvider } from '@/infrastructure/api'
import { ClientProtectedRoute } from '@/domains/auth'
import Navbar from '@/shared/layouts/components/navigation/Navbar'
import SubNavbar from '@/shared/layouts/components/navigation/SubNavbar'
import AppFooter from '@/shared/layouts/components/navigation/Footer'
import { useAppSelector, useAppDispatch } from '@/shared/providers/hooks'
import {
  selectIsMobile,
  selectSidebarHiddenOnMobile,
  setIsMobile,
  setSidebarHiddenOnMobile,
  setMainSidebarCollapsed,
} from '@/shared/providers/state/navigationSlice'
import { useNavigationHydration } from '@/shared/providers/state/useNavigationHydration'
import ShineLayout from '@/shared/layouts/components/dashboard/ShineLayout'
import { usePathname, useRouter } from 'next/navigation'
import SidebarWithShine from '@/shared/layouts/components/sidebar/SidebarWithShine'
import SafeLoadingOverlay from '@/shared/components/feedback/SafeLoadingOverlay'
import GlobalErrorBoundary from '@/shared/components/error/GlobalErrorBoundary'
import { ToastNotificationsProvider } from '@/shared/components/feedback/ToastNotifications'
import { SessionTimeoutWarning } from '@/shared/components/security/SessionTimeoutWarning'
import { SecurityBanner } from '@/shared/components/security/SecurityBanner'
import { SignOutScreen } from '@/shared/components/feedback/SignOutScreen'
import {
  isSigningOut,
  clearSigningOutFlag,
  hardNavigateToLogin,
  SIGNING_OUT_TTL_MS,
} from '@/domains/auth/utils/sign-out'

import '@/shared/components/loading.css'
import { isChromeFreeRoute } from './isChromeFreeRoute'

const { Header, Content, Footer } = ShineLayout

const MAIN_CONTENT_ID = 'main-content'
/** Phone / small tablet — sidebar becomes off-canvas drawer */
const MOBILE_BREAKPOINT = 768
/** Tablet landscape / small laptop — docked icon rail (more content width) */
const TABLET_BREAKPOINT = 1024

interface SubNavbarContextType {
  pageActions: React.ReactNode | null
  toolbarContent: React.ReactNode | null
  setPageActions: (actions: React.ReactNode | null) => void
  setToolbarContent: (content: React.ReactNode | null) => void
  isMobile?: boolean
  mobileSidebarOpen?: boolean
  setMobileSidebarOpen?: (open: boolean) => void
}

const SubNavbarContext = createContext<SubNavbarContextType | undefined>(undefined)

export function useSubNavbar() {
  const context = useContext(SubNavbarContext)
  if (context === undefined) {
    throw new Error('useSubNavbar must be used within AppShellLayout')
  }
  return context
}

interface AppShellLayoutProps {
  children: React.ReactNode
  pageActions?: React.ReactNode | null
  toolbarContent?: React.ReactNode | null
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(mq.matches)
    update()
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])

  return reduced
}

/** First Tab target — styles live in globals.css (.skip-to-content + --color-ring). */
function SkipToContentLink() {
  return (
    <a
      href={`#${MAIN_CONTENT_ID}`}
      className="skip-to-content"
      onClick={(e) => {
        e.preventDefault()
        const target = document.getElementById(MAIN_CONTENT_ID)
        if (target) {
          target.focus({ preventScroll: false })
          target.scrollIntoView({ block: 'start' })
        }
      }}
    >
      Skip to content
    </a>
  )
}

function AppShellLayoutContent({
  children,
  pageActions,
  toolbarContent,
}: AppShellLayoutProps) {
  const { token, isDark } = useTheme()
  const dispatch = useAppDispatch()
  const isMobile = useAppSelector(selectIsMobile)
  const sidebarHiddenOnMobile = useAppSelector(selectSidebarHiddenOnMobile)
  const pathname = usePathname()
  const router = useRouter()
  const prefersReducedMotion = usePrefersReducedMotion()
  // Ephemeral sign-out overlay — only while TTL flag is active (never permanent)
  const [signingOut, setSigningOut] = useState(false)
  useEffect(() => {
    // Auth routes: drop overlay only (flag cleared by auth layout after hard load)
    if (pathname?.startsWith('/auth')) {
      setSigningOut(false)
      return
    }

    const tick = () => {
      const active = isSigningOut()
      setSigningOut(active)
      // Safety: if still flagged near TTL, force login so UI cannot stick
      if (!active) return
      try {
        const raw = sessionStorage.getItem('mechagora:signing_out')
        const started = raw && raw !== '1' ? Number(raw) : 0
        if (started > 0 && Date.now() - started > SIGNING_OUT_TTL_MS - 500) {
          clearSigningOutFlag()
          setSigningOut(false)
          hardNavigateToLogin('client')
        }
      } catch {
        /* no-op */
      }
    }

    tick()
    const id = window.setInterval(tick, 150)
    return () => window.clearInterval(id)
  }, [pathname])

  // Dev/prod: keep login route hot so sign-out → /auth/login is not a cold compile
  useEffect(() => {
    try {
      router.prefetch('/auth/login')
    } catch {
      /* older routers */
    }
  }, [router, pathname])

  // Defense-in-depth: ConditionalLayout already excludes chrome-free routes
  const showChrome = !isChromeFreeRoute(pathname)

  /**
   * Panel-lock routes: outer shell does not scroll; page content owns height and
   * only internal regions (e.g. chat transcript) scroll.
   */
  const isTradingDeskRoute = pathname === '/trading'
  const isAgentsDeskRoute =
    pathname === '/ai-trading' || Boolean(pathname?.startsWith('/ai-trading/'))

  useEffect(() => {
    const root = document.documentElement
    if (isTradingDeskRoute) {
      root.setAttribute('data-trading-desk', '1')
    } else {
      root.removeAttribute('data-trading-desk')
    }
    return () => root.removeAttribute('data-trading-desk')
  }, [isTradingDeskRoute])
  const isPanelScrollRoute =
    pathname === '/chat-ai' ||
    Boolean(pathname?.startsWith('/chat-ai/')) ||
    isTradingDeskRoute ||
    isAgentsDeskRoute

  const mobileSidebarOpen = isMobile && !sidebarHiddenOnMobile

  useNavigationHydration()

  // Viewport breakpoints — single passive listener (375 / 768 / 1024 targets)
  // Mobile (<768): drawer. Tablet (768–1023): docked + collapsed rail.
  // Desktop (≥1024): docked, respect user collapse preference (not forced).
  useEffect(() => {
    let lastMobile: boolean | null = null
    let lastTablet: boolean | null = null

    const checkViewport = () => {
      const width = window.innerWidth
      const nextMobile = width < MOBILE_BREAKPOINT
      const nextTablet = !nextMobile && width < TABLET_BREAKPOINT

      if (lastMobile !== nextMobile) {
        lastMobile = nextMobile
        dispatch(setIsMobile(nextMobile))
      }

      // Auto icon-rail on tablet only when crossing into/out of tablet band
      if (lastTablet !== nextTablet) {
        lastTablet = nextTablet
        if (nextTablet) {
          dispatch(setMainSidebarCollapsed(true))
        }
      }
    }

    checkViewport()
    window.addEventListener('resize', checkViewport, { passive: true })
    return () => window.removeEventListener('resize', checkViewport)
  }, [dispatch])

  // Close mobile drawer on route change
  useEffect(() => {
    if (isMobile && !sidebarHiddenOnMobile) {
      dispatch(setSidebarHiddenOnMobile(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to pathname
  }, [pathname, dispatch])

  // Escape closes mobile sidebar; focus trap light-touch (close only)
  useEffect(() => {
    if (!mobileSidebarOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch(setSidebarHiddenOnMobile(true))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileSidebarOpen, dispatch])

  const closeMobileSidebar = useCallback(() => {
    dispatch(setSidebarHiddenOnMobile(true))
  }, [dispatch])

  // Symmetric horizontal pad: sand gutters left + right of the ivory desk.
  // Scrollbar still sits on the column edge via scrollbar-gutter: stable both-edges on
  // .shell-scroll-region (do not use padding-right: 0 — content looked flush).
  // Panel-lock (chat): tighter bottom pad so the desk fills the viewport.
  const contentPadding =
    isTradingDeskRoute || isAgentsDeskRoute
      ? '0'
      : isPanelScrollRoute
        ? isMobile
          ? '8px 10px 10px 10px'
          : '10px 16px 12px 16px'
        : isMobile
          ? '12px 14px 20px 14px'
          : '16px 24px 28px 24px'

  // Sidebar without shine/brush overlays — single chrome surface
  const sidebarNode = <SidebarWithShine shineEnabled={false} />

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: token.colorPrimary,
          colorBgBase: token.colorBgBase,
          colorBgContainer: token.colorBgContainer,
          colorBorder: token.colorBorder,
          colorText: token.colorText,
          colorTextSecondary: token.colorTextSecondary,
          fontFamily: token.fontFamily,
          fontSize: token.fontSize,
          borderRadius: token.borderRadius,
        },
      }}
    >
      <SafeLoadingOverlay />
      {showChrome && <SessionTimeoutWarning />}
      <SecurityBanner />
      {/* Immediate full-viewport professional sign-out (before hard nav) */}
      {signingOut && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
          }}
        >
          <SignOutScreen />
        </div>
      )}
      {/* ThemePortal floating FAB removed — theme toggle lives in header chrome only */}

      {showChrome && (
        <SkipToContentLink />
      )}

      <ShineLayout
        style={{
          minHeight: '100vh',
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          boxSizing: 'border-box',
          position: 'relative',
          width: '100%',
          maxWidth: '100vw',
        }}
        className="main-layout-container dashboard-scroll-container shell-root"
        data-trading-desk={isTradingDeskRoute ? '1' : undefined}
        shine={false}
      >
        {/* Desktop/tablet: docked sidebar in flex row (minWidth:0 main column prevents x-scroll) */}
        {showChrome && !isMobile && (
          <div className="shell-docked-sidebar">
            {sidebarNode}
          </div>
        )}

        {/* Mobile (<768): collapsible drawer + scrim — does not reflow main column */}
        {showChrome && mobileSidebarOpen && (
          <>
            <div
              role="presentation"
              aria-hidden
              onClick={closeMobileSidebar}
              className="shell-mobile-scrim"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1500,
                /* Warm espresso scrim — not cold slate (login craft floor) */
                background: isDark
                  ? 'color-mix(in srgb, var(--color-background, #1A1411) 72%, transparent)'
                  : 'color-mix(in srgb, var(--shell-sidebar-bg, #231C18) 48%, transparent)',
                transition: prefersReducedMotion ? undefined : 'opacity 160ms ease',
              }}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Primary navigation"
              className="shell-mobile-sidebar"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 1600,
                height: '100dvh',
                width: 'min(100vw - 48px, 280px)',
                maxWidth: 'min(100vw, 280px)',
                overflow: 'hidden',
                boxShadow: token.boxShadowSecondary,
              }}
            >
              {sidebarNode}
            </div>
          </>
        )}

        {/* Main column: header + single scroll content + footer */}
        <ShineLayout
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            overflowX: 'hidden',
            minHeight: 0,
            flex: 1,
            minWidth: 0,
            position: 'relative',
            zIndex: 0,
            width: '100%',
            maxWidth: '100%',
          }}
          shine={false}
        >
          {showChrome && (
            <Header
              className="app-header"
              style={{
                padding: 0,
                height: 'auto',
                lineHeight: 'normal',
                flexShrink: 0,
                /* Fine-tech chrome — flat; Navbar/SubNavbar own hairlines */
                background: 'var(--shell-navbar-bg, #FFFEFC)',
                boxShadow: 'none',
                position: 'relative',
                zIndex: 1400,
                width: '100%',
                maxWidth: '100%',
                overflow: 'visible',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                }}
              >
                <Navbar />
                <SubNavbar>
                  {pageActions && <div className="page-actions">{pageActions}</div>}
                  {toolbarContent && (
                    <div className="toolbar-content">{toolbarContent}</div>
                  )}
                </SubNavbar>
              </div>
            </Header>
          )}

          <Content
            id={MAIN_CONTENT_ID}
            className="layout-content"
            tabIndex={-1}
            role="main"
            aria-label="Main content"
            style={{
              padding: 0,
              margin: 0,
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              /* Sand instrument bay — always solid (never Ant white void) */
              background: 'var(--shell-content-bg, #E0D2C2)',
              backgroundColor: 'var(--shell-content-bg, #E0D2C2)',
              position: 'relative',
              zIndex: 0,
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {/* Scroll owner: page scroll OR panel-lock (chat — internal only) */}
            <div
              data-scrollable={isPanelScrollRoute ? 'false' : 'true'}
              data-shell-scroll="true"
              data-shell-scroll-mode={isPanelScrollRoute ? 'panel' : 'page'}
              className="shell-scroll-region custom-scrollbar"
              style={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: isPanelScrollRoute ? 'hidden' : 'scroll',
                overflowX: 'hidden',
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: 0,
                background: 'var(--shell-content-bg, #E0D2C2)',
                backgroundColor: 'var(--shell-content-bg, #E0D2C2)',
                WebkitOverflowScrolling: isPanelScrollRoute
                  ? undefined
                  : 'touch',
              }}
            >
              <div
                className="shell-content-pad"
                style={{
                  padding: contentPadding,
                  /* page: size to content · panel: fill viewport, no outer scroll */
                  flex: isPanelScrollRoute ? '1 1 auto' : '0 0 auto',
                  minHeight: isPanelScrollRoute ? 0 : '100%',
                  height: isPanelScrollRoute ? '100%' : 'auto',
                  maxHeight: isPanelScrollRoute ? '100%' : undefined,
                  minWidth: 0,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: isPanelScrollRoute ? 'hidden' : 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'var(--shell-content-bg, #E0D2C2)',
                  backgroundColor: 'var(--shell-content-bg, #E0D2C2)',
                }}
              >
                {/* Ivory desk on sand well — full bleed, no max-width card */}
                <div
                  className="shell-content-workspace"
                  style={
                    isPanelScrollRoute
                      ? {
                        flex: '1 1 auto',
                        minHeight: 0,
                        height: '100%',
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }
                      : undefined
                  }
                >
                  {children}
                  {showChrome && isMobile && !isTradingDeskRoute && !isAgentsDeskRoute ? (
                    <Footer
                      className="app-footer"
                      style={{
                        padding: 0,
                        flexShrink: 0,
                        marginTop: 'auto',
                        background: 'var(--shell-footer-bg, #FFFEFC)',
                        boxShadow: 'none',
                        position: 'relative',
                        zIndex: 2,
                      }}
                    >
                      <AppFooter />
                    </Footer>
                  ) : null}
                </div>
              </div>
            </div>
          </Content>

          {showChrome && !isMobile && !isTradingDeskRoute && !isAgentsDeskRoute ? (
            <Footer
              className="app-footer"
              style={{
                padding: 0,
                flexShrink: 0,
                /* Fine-tech bookend — Footer owns hairline; no double chrome */
                background: 'var(--shell-footer-bg, #FFFEFC)',
                boxShadow: 'none',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <AppFooter />
            </Footer>
          ) : null}
        </ShineLayout>
      </ShineLayout>
    </ConfigProvider>
  )
}

export default function AppShellLayout({
  children,
  pageActions,
  toolbarContent,
}: AppShellLayoutProps) {
  const [pageActionsState, setPageActions] = useState<React.ReactNode | null>(
    pageActions || null
  )
  const [toolbarContentState, setToolbarContent] = useState<React.ReactNode | null>(
    toolbarContent || null
  )

  const isMobile = useAppSelector(selectIsMobile)
  const sidebarHiddenOnMobile = useAppSelector(selectSidebarHiddenOnMobile)
  const dispatch = useAppDispatch()

  const mobileSidebarOpen = isMobile && !sidebarHiddenOnMobile
  const setMobileSidebarOpen = useCallback(
    (open: boolean) => {
      // open=true → show sidebar; open=false → hide
      dispatch(setSidebarHiddenOnMobile(!open))
    },
    [dispatch]
  )

  const contextValue = useMemo(
    () => ({
      pageActions: pageActionsState,
      toolbarContent: toolbarContentState,
      setPageActions,
      setToolbarContent,
      isMobile,
      mobileSidebarOpen,
      setMobileSidebarOpen,
    }),
    [
      pageActionsState,
      toolbarContentState,
      isMobile,
      mobileSidebarOpen,
      setMobileSidebarOpen,
    ]
  )

  return (
    <SubNavbarContext.Provider value={contextValue}>
      <AppShellLayoutContent
        pageActions={pageActionsState}
        toolbarContent={toolbarContentState}
      >
        {children}
      </AppShellLayoutContent>
    </SubNavbarContext.Provider>
  )
}

/**
 * App section provider stack — client portal only (no employee routes here).
 *
 * Root `Providers` already supplies AntdRegistry, Redux, Theme, and Auth —
 * do not re-wrap those here (avoids nested store/theme thrash).
 *
 * ClientProtectedRoute gates the whole chrome. ClientDeskSessionProvider
 * wraps the shell so Sidebar Pulse and desk pages share one workspace.
 */
export function AppSectionLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isDashboardDesignPreview =
    process.env.NODE_ENV === 'development' &&
    pathname === '/client/dashboard/preview'

  const desk = (
    <ClientDeskSessionProvider>
      <AppShellLayout>{children}</AppShellLayout>
    </ClientDeskSessionProvider>
  )

  return (
    <GlobalErrorBoundary>
      <ApiProvider>
        <LoadingProvider>
          <PageProvider>
            <ToastNotificationsProvider>
              {isDashboardDesignPreview ? desk : (
                <ClientProtectedRoute>{desk}</ClientProtectedRoute>
              )}
            </ToastNotificationsProvider>
          </PageProvider>
        </LoadingProvider>
      </ApiProvider>
    </GlobalErrorBoundary>
  )
}
